<section class="relative flex flex-col items-center w-full">
  <div class="relative flex flex-col w-full max-w-5xl gap-6 px-2">
    <header>
      <h1 class="font-display text-2xl md:text-3xl font-bold text-gray-900">Suivi fiscal</h1>
      <p class="text-gray-500 mt-1">Calendrier fiscal et informations de vos clients.</p>
    </header>

    <article class="rounded-xl border border-gray-200 bg-white overflow-hidden">
      @if (loading()) {
        <p class="p-5 text-sm text-gray-500">Chargement...</p>
      } @else if (!rows().length) {
        <p class="p-5 text-sm text-gray-500">Aucun client à suivre.</p>
      } @else {
        <div class="space-y-3 p-3">
          @for (row of rows(); track row.company.siret) {
            <div class="rounded-xl border border-gray-200 overflow-hidden">
              <button
                type="button"
                class="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left cursor-pointer"
                (click)="toggleExpanded(row.company.siret)"
              >
                <div>
                  <p class="font-medium text-gray-900">{{ row.company.nomEntreprise }}</p>
                  <p class="text-xs text-gray-500">{{ row.company.siretFormatte }}</p>
                </div>
                <div class="flex items-center gap-2 text-xs text-gray-500">
                  @if (row.taxCalendar.length > 0) {
                    <span>{{ row.taxCalendar.length }} période(s)</span>
                  } @else if (row.detail.company.dateInfoFiscale) {
                    <span>Calendrier indisponible</span>
                  } @else if (hasConfiguredCredentials(row.detail)) {
                    <span class="text-amber-600">Disponible dès demain</span>
                  } @else {
                    <span>Identifiants requis</span>
                  }
                  <span class="material-symbols-outlined text-base">
                    {{ isExpanded(row.company.siret) ? 'expand_less' : 'expand_more' }}
                  </span>
                </div>
              </button>

              @if (isExpanded(row.company.siret)) {
                <div class="border-t border-gray-200 p-4 bg-gray-50">
                  <section class="flex flex-col lg:flex-row gap-6">
                    <div class="flex flex-col bg-white rounded-md py-6 px-6 shadow-sm lg:w-1/3">
                      <div class="flex items-center gap-2 pb-4">
                        <lucide-icon [name]="Landmark" size="20" class="text-gray-700"></lucide-icon>
                        <h2 class="text-gray-700 text-lg font-semibold">Informations fiscales</h2>
                      </div>
                      <div class="space-y-3 text-sm flex-1">
                        <div class="flex flex-col gap-1">
                          <span class="text-gray-500 text-xs uppercase tracking-wide font-medium">Dette fiscale</span>
                          @if (row.detail.company.detteFiscale) {
                            <span class="font-medium text-gray-800">{{ row.detail.company.detteFiscale }}</span>
                          } @else if (row.detail.company.dateInfoFiscale) {
                            <span class="text-gray-400 italic flex items-center gap-1">
                              <lucide-icon [name]="AlertCircle" size="13"></lucide-icon>
                              Information indisponible
                            </span>
                          } @else if (hasConfiguredCredentials(row.detail)) {
                            <span class="text-amber-600 italic flex items-center gap-1">
                              <lucide-icon [name]="Clock" size="13"></lucide-icon>
                              Disponible dès demain
                            </span>
                          } @else {
                            <span class="text-gray-400 italic flex items-center gap-1">
                              <lucide-icon [name]="AlertCircle" size="13"></lucide-icon>
                              Identifiants Impots.gouv requis
                            </span>
                          }
                        </div>
                        <div class="flex flex-col gap-1">
                          <span class="text-gray-500 text-xs uppercase tracking-wide font-medium">Régime d'imposition</span>
                          @if (row.detail.company.regimeImposition) {
                            <span class="font-medium text-gray-800">{{ row.detail.company.regimeImposition }}</span>
                          } @else if (row.detail.company.dateInfoFiscale) {
                            <span class="text-gray-400 italic flex items-center gap-1">
                              <lucide-icon [name]="AlertCircle" size="13"></lucide-icon>
                              Information indisponible
                            </span>
                          } @else if (hasConfiguredCredentials(row.detail)) {
                            <span class="text-amber-600 italic flex items-center gap-1">
                              <lucide-icon [name]="Clock" size="13"></lucide-icon>
                              Disponible dès demain
                            </span>
                          } @else {
                            <span class="text-gray-400 italic flex items-center gap-1">
                              <lucide-icon [name]="AlertCircle" size="13"></lucide-icon>
                              Identifiants Impots.gouv requis
                            </span>
                          }
                        </div>
                        @if (row.detail.company.dateInfoFiscale) {
                          <div class="pt-2 border-t border-gray-100">
                            <span class="text-xs text-gray-400">
                              Mis à jour le {{ row.detail.company.dateInfoFiscale | date: 'dd/MM/yyyy à HH:mm' }}
                            </span>
                          </div>
                        }
                      </div>
                    </div>

                    <div class="flex flex-col bg-white rounded-md py-6 px-6 shadow-sm lg:w-2/3">
                      <div class="flex items-center gap-2 pb-4">
                        <lucide-icon [name]="Calendar" size="20" class="text-gray-700"></lucide-icon>
                        <h2 class="text-gray-700 text-lg font-semibold">Calendrier fiscal</h2>
                      </div>
                      @if (row.taxCalendar.length === 0) {
                        <div class="flex flex-col items-center justify-center py-6 text-center">
                          <lucide-icon [name]="Calendar" size="32" class="mb-2 text-gray-300"></lucide-icon>
                          @if (row.detail.company.dateInfoFiscale) {
                            <span class="text-sm text-gray-500 font-medium">Information indisponible</span>
                          } @else if (hasConfiguredCredentials(row.detail)) {
                            <span class="text-sm text-amber-600 font-medium">Disponible dès demain</span>
                          } @else {
                            <span class="text-sm text-gray-500">Identifiants Impots.gouv requis</span>
                          }
                        </div>
                      } @else {
                        <div class="overflow-hidden rounded-lg border border-gray-200">
                          <table class="w-full text-sm">
                            <thead>
                              <tr class="bg-gray-50 text-left text-gray-600">
                                <th class="px-4 py-2.5 font-medium">Période d'imposition</th>
                                <th class="px-4 py-2.5 font-medium">Type de déclaration</th>
                                <th class="px-4 py-2.5 font-medium">Date limite</th>
                                <th class="px-4 py-2.5 font-medium">État</th>
                              </tr>
                            </thead>
                            <tbody>
                              @for (entry of row.taxCalendar; track entry.id) {
                                <tr class="border-t border-gray-100 hover:bg-gray-50">
                                  <td class="px-4 py-3 font-medium text-gray-800">{{ entry.periodeImposition }}</td>
                                  <td class="px-4 py-3 text-gray-600">{{ entry.declarationType }}</td>
                                  <td class="px-4 py-3 text-gray-600">{{ entry.dateLimite }}</td>
                                  <td class="px-4 py-3">
                                    <span
                                      class="text-xs px-2 py-0.5 rounded font-medium"
                                      [class]="entry.etatPeriode?.toLowerCase()?.includes('soldé') || entry.etatPeriode?.toLowerCase()?.includes('solde')
                                        ? 'bg-green-100 text-green-700'
                                        : entry.etatPeriode?.toLowerCase()?.includes('retard')
                                          ? 'bg-red-100 text-red-700'
                                          : 'bg-secondary/10 text-secondary'"
                                    >
                                      {{ entry.etatPeriode }}
                                    </span>
                                  </td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      }
                    </div>
                  </section>
                </div>
              }
            </div>
          }
        </div>
      }
    </article>
  </div>
</section>
